<template>
  <SidebarComponent />

  <div class="dashboard-layout">
    <div class="content-container">
      <div class="card-container">
        <div class="titolo-card">
          <h1>Scalata consulenze tecniche</h1>
          <p class="muted">
            Scansiona tutte le consulenze tecniche ancora in stato "Da fare" che hanno gia il vero check finale completato.
            Il questionario iniziale convertito in check non viene contato come check finale.
          </p>
        </div>

        <div v-if="!initialized" class="tool-state">Caricamento autorizzazioni…</div>
        <div v-else-if="!isAllowed" class="tool-state tool-state--error">
          Accesso non autorizzato a questo strumento.
        </div>
        <template v-else>
          <section class="tool-card">
            <div class="toolbar">
              <div class="toolbar-group">
                <span class="toolbar-label">Ultima scansione</span>
                <strong>{{ lastScanLabel }}</strong>
              </div>
              <div class="toolbar-group">
                <span class="toolbar-label">Ultima operazione</span>
                <strong>{{ latestOperationLabel }}</strong>
                <small v-if="latestOperation" class="muted">
                  {{ latestOperationSummary }}
                </small>
              </div>
              <div class="toolbar-group toolbar-group--wide">
                <span class="toolbar-label">Regola applicata</span>
                <small class="muted">
                  Tipo vuoto trattato come tecnica. Se il vero check finale del programma e fatto, la consulenza tecnica "Da fare" diventa "Scalata".
                </small>
              </div>
            </div>

            <div class="toolbar-actions">
              <button class="btn primary staff-button-neutral" type="button" :disabled="loading || applying || restoring" @click="scanCandidates">
                {{ loading ? 'Scansione…' : 'Ricarica scansione' }}
              </button>
              <button class="btn success" type="button" :disabled="!canApply" @click="applyChanges">
                {{ applying ? 'Applico…' : `Applica ${rows.length} modifiche` }}
              </button>
              <button class="btn warning" type="button" :disabled="!canRestore" @click="restoreLatestOperation">
                {{ restoring ? 'Ripristino…' : 'Ripristina ultima operazione' }}
              </button>
            </div>
          </section>

          <div class="stats-grid">
            <div class="stat-card">
              <span class="stat-card__label">Percorsi letti</span>
              <strong>{{ scanStats.percorsiScanned }}</strong>
            </div>
            <div class="stat-card">
              <span class="stat-card__label">Con check finale fatto</span>
              <strong>{{ scanStats.percorsiWithFinalCheckDone }}</strong>
            </div>
            <div class="stat-card stat-card--accent">
              <span class="stat-card__label">Consulenze da scalare</span>
              <strong>{{ rows.length }}</strong>
            </div>
            <div class="stat-card">
              <span class="stat-card__label">Ultime applicate</span>
              <strong>{{ latestOperation?.totalApplied || 0 }}</strong>
            </div>
          </div>

          <div v-if="statusMessage" class="tool-state tool-state--success">{{ statusMessage }}</div>
          <div v-if="error" class="tool-state tool-state--error">{{ error }}</div>

          <div v-if="loading" class="loader"><h4>Scansione in corso…</h4></div>
          <div v-else-if="!rows.length" class="tool-state">
            Nessuna consulenza tecnica da modificare trovata con la regola corrente.
          </div>
          <div v-else class="table-wrap">
            <table class="consulenze-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Utente</th>
                  <th>Programma</th>
                  <th>Sequenza</th>
                  <th>Consulenza</th>
                  <th>Check finale</th>
                  <th>Status attuale</th>
                  <th>Step</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in rows" :key="row.rowKey">
                  <td class="mono">{{ index + 1 }}</td>
                  <td>
                    <div class="cell-main">{{ row.userEmail || '—' }}</div>
                    <small class="muted mono">{{ row.uid || 'UID non disponibile' }}</small>
                  </td>
                  <td>
                    <div class="cell-main">{{ row.programTitle }}</div>
                    <small class="muted">{{ row.productName || '—' }}</small>
                  </td>
                  <td>{{ row.sequenceLabel || '—' }}</td>
                  <td>
                    <div class="cell-main">{{ row.consulenzaTypeLabel }}</div>
                    <small class="muted">{{ row.stepName }}</small>
                  </td>
                  <td>
                    <div class="cell-main">{{ row.checkLabel }}</div>
                    <small class="muted">{{ formatDateTime(row.checkDoneAt) || 'Data non disponibile' }}</small>
                  </td>
                  <td>
                    <span class="status-pill status-pill--todo">{{ row.consulenzaStatus }}</span>
                  </td>
                  <td class="mono">consulenza #{{ row.stepIndex + 1 }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import {
  addDoc,
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  startAfter,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import SidebarComponent from '@/components/SidebarComponent.vue';
import {
  applyAutomaticTechnicalConsulenzeScalata,
  getRealCheckFinaleDoneEntry as getPercorsoRealCheckFinaleDoneEntry,
  isAutoScalabileTechnicalConsulenzaStep,
  normalizeConsulenzaStatus as normalizePercorsoConsulenzaStatus,
  normalizeConsulenzaType as normalizePercorsoConsulenzaType,
} from '@/lib/percorsi';

const PAGE_SIZE = 250;
const WRITE_BATCH_SIZE = 300;
const OPERATION_CHUNK_SIZE = 120;
const OPERATIONS_COLLECTION = 'consulenze_mass_actions';
const OPERATION_KIND = 'technical_check_done_to_scalata_v1';

const toDateSafe = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') {
    const date = value.toDate();
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const normalizeText = (value) => {
  if (value === undefined || value === null) return '';
  return String(value).trim();
};

const chunkItems = (items, size) => {
  const chunks = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
};

export default {
  name: 'ConsulenzeTecnicheScalataTool',
  components: {
    SidebarComponent,
  },
  data() {
    return {
      db: getFirestore(),
      rows: [],
      loading: false,
      applying: false,
      restoring: false,
      error: '',
      statusMessage: '',
      lastScan: null,
      initialized: false,
      userEmail: '',
      userUid: '',
      authUnsub: null,
      latestOperation: null,
      scanStats: {
        percorsiScanned: 0,
        percorsiWithFinalCheckDone: 0,
      },
      allowedEmails: ['valeriobianconi@gmail.com', 'robertacurto@fusco.fit', 'assistenza@be-better.fit'],
    };
  },
  computed: {
    isAllowed() {
      return this.allowedEmails.includes(this.userEmail);
    },
    lastScanLabel() {
      return this.formatDateTime(this.lastScan) || '—';
    },
    latestOperationLabel() {
      if (!this.latestOperation) return 'Nessuna';
      if (this.latestOperation.restoredAt) return `Ripristinata ${this.formatDateTime(this.latestOperation.restoredAt)}`;
      if (this.latestOperation.status === 'failed') return 'Fallita';
      if (this.latestOperation.status === 'completed') return `Applicata ${this.formatDateTime(this.latestOperation.appliedAt || this.latestOperation.createdAt)}`;
      return 'In corso';
    },
    latestOperationSummary() {
      if (!this.latestOperation) return '';
      const totalApplied = Number(this.latestOperation.totalApplied || 0);
      const totalPercorsi = Number(this.latestOperation.totalPercorsi || 0);
      return `${totalApplied} consulenze su ${totalPercorsi} programmi`;
    },
    canApply() {
      return this.isAllowed && !this.loading && !this.applying && !this.restoring && this.rows.length > 0;
    },
    canRestore() {
      const op = this.latestOperation;
      return this.isAllowed
        && !this.loading
        && !this.applying
        && !this.restoring
        && !!op
        && op.status === 'completed'
        && !op.restoredAt
        && Number(op.totalApplied || 0) > 0;
    },
  },
  mounted() {
    const auth = getAuth();
    this.authUnsub = onAuthStateChanged(auth, async (user) => {
      this.userEmail = normalizeText(user?.email).toLowerCase();
      this.userUid = normalizeText(user?.uid);
      if (this.initialized) return;
      this.initialized = true;
      if (!this.isAllowed) return;
      await this.loadLatestOperation();
      await this.scanCandidates();
    });
  },
  beforeUnmount() {
    if (typeof this.authUnsub === 'function') {
      this.authUnsub();
      this.authUnsub = null;
    }
  },
  methods: {
    resetFeedback() {
      this.error = '';
      this.statusMessage = '';
    },
    formatDateTime(value) {
      const date = toDateSafe(value);
      if (!date) return '';
      return new Intl.DateTimeFormat('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    },
    formatSequenceLabel(sequenceIndex, sequenceTotal) {
      const index = Number(sequenceIndex);
      const total = Number(sequenceTotal);
      if (Number.isFinite(index) && Number.isFinite(total) && total > 0) {
        return `${index}/${total}`;
      }
      return '';
    },
    stepKeyFromName(name = '') {
      const normalized = normalizeText(name).toLowerCase();
      if (normalized.includes('check') || normalized.includes('scadenza')) return 'check_finale';
      if (normalized.includes('questionario')) return 'questionario';
      if (normalized.includes('consegna')) return 'consegna';
      if (normalized.includes('consulenza')) return 'consulenza';
      return null;
    },
    normalizeConsulenzaStatus(value) {
      return normalizePercorsoConsulenzaStatus(value);
    },
    isConsulenzaStatusDone(value) {
      const normalized = this.normalizeConsulenzaStatus(value);
      return normalized === 'Fatta' || normalized === 'Rifiutata' || normalized === 'Scalata';
    },
    normalizeConsulenzaType(value) {
      return normalizePercorsoConsulenzaType(value);
    },
    describeConsulenzaType(value) {
      const normalized = this.normalizeConsulenzaType(value);
      if (normalized === 'nutrizionale') return 'Nutrizionale';
      if (normalized === 'tecnica') return 'Tecnica';
      return 'Tecnica (tipo vuoto)';
    },
    getRealCheckFinaleDoneEntry(steps = []) {
      return getPercorsoRealCheckFinaleDoneEntry(steps);
    },
    isCandidateConsulenzaStep(step = {}) {
      return isAutoScalabileTechnicalConsulenzaStep(step);
    },
    buildCandidateRow(docSnap, data, step, stepIndex, checkEntry) {
      const title = normalizeText(data.title) || normalizeText(data.productName) || 'Programma';
      const userEmail = normalizeText(data.userEmail);
      const uid = normalizeText(data.uid);
      return {
        rowKey: `${docSnap.id}__${stepIndex}`,
        percorsoId: docSnap.id,
        uid,
        userEmail,
        productName: normalizeText(data.productName),
        programTitle: title,
        sequenceIndex: Number(data.sequenceIndex) || null,
        sequenceTotal: Number(data.sequenceTotal) || null,
        sequenceLabel: this.formatSequenceLabel(data.sequenceIndex, data.sequenceTotal),
        stepIndex,
        stepName: normalizeText(step.name) || 'Consulenza',
        rawConsulenzaType: normalizeText(step.consulenzaType),
        consulenzaTypeLabel: this.describeConsulenzaType(step.consulenzaType),
        consulenzaStatus: this.normalizeConsulenzaStatus(step.consulenzaStatus),
        checkIndex: checkEntry.index,
        checkLabel: normalizeText(checkEntry.step?.name) || 'Check finale',
        checkDoneAt: checkEntry.step?.doneAt || null,
      };
    },
    async scanCandidates() {
      this.resetFeedback();
      this.loading = true;
      this.rows = [];
      this.scanStats = {
        percorsiScanned: 0,
        percorsiWithFinalCheckDone: 0,
      };

      try {
        const nextRows = [];
        let cursor = null;
        let keepGoing = true;

        while (keepGoing) {
          let percorsiQuery = query(
            collection(this.db, 'percorsi'),
            orderBy(documentId()),
            limit(PAGE_SIZE),
          );
          if (cursor) {
            percorsiQuery = query(
              collection(this.db, 'percorsi'),
              orderBy(documentId()),
              startAfter(cursor),
              limit(PAGE_SIZE),
            );
          }

          const snap = await getDocs(percorsiQuery);
          if (snap.empty) break;

          snap.forEach((docSnap) => {
            this.scanStats.percorsiScanned += 1;
            const data = docSnap.data() || {};
            const steps = Array.isArray(data.steps) ? data.steps : [];
            const checkEntry = this.getRealCheckFinaleDoneEntry(steps);
            if (!checkEntry) return;

            this.scanStats.percorsiWithFinalCheckDone += 1;
            steps.forEach((step, stepIndex) => {
              if (!this.isCandidateConsulenzaStep(step)) return;
              nextRows.push(this.buildCandidateRow(docSnap, data, step, stepIndex, checkEntry));
            });
          });

          cursor = snap.docs[snap.docs.length - 1];
          if (snap.size < PAGE_SIZE) {
            keepGoing = false;
          }
        }

        this.rows = nextRows;
        this.lastScan = new Date();
        if (!nextRows.length) {
          this.statusMessage = 'Scansione completata: nessuna consulenza da scalare trovata.';
        }
      } catch (err) {
        console.error('scanCandidates error', err);
        this.error = 'Errore durante la scansione delle consulenze tecniche.';
      } finally {
        this.loading = false;
      }
    },
    mapOperationDoc(docSnap) {
      const data = docSnap.data() || {};
      return {
        id: docSnap.id,
        ...data,
        createdAt: toDateSafe(data.createdAt),
        appliedAt: toDateSafe(data.appliedAt),
        restoredAt: toDateSafe(data.restoredAt),
      };
    },
    async loadLatestOperation() {
      try {
        const opQuery = query(
          collection(this.db, OPERATIONS_COLLECTION),
          orderBy('createdAt', 'desc'),
          limit(20),
        );
        const snap = await getDocs(opQuery);
        const match = snap.docs
          .map((docSnap) => this.mapOperationDoc(docSnap))
          .find((item) => item.kind === OPERATION_KIND) || null;
        this.latestOperation = match;
      } catch (err) {
        console.error('loadLatestOperation error', err);
        this.latestOperation = null;
      }
    },
    async saveOperationChunks(operationId, items) {
      if (!operationId || !items.length) return;
      const chunks = chunkItems(items, OPERATION_CHUNK_SIZE);
      for (let index = 0; index < chunks.length; index += 1) {
        const chunkRef = doc(this.db, OPERATIONS_COLLECTION, operationId, 'chunks', `chunk_${String(index).padStart(4, '0')}`);
        await setDoc(chunkRef, {
          index,
          itemCount: chunks[index].length,
          items: chunks[index],
          createdAt: serverTimestamp(),
        });
      }
    },
    groupRowsByPercorso(items = []) {
      const grouped = new Map();
      items.forEach((item) => {
        const key = normalizeText(item.percorsoId);
        if (!key) return;
        const list = grouped.get(key) || [];
        list.push(item);
        grouped.set(key, list);
      });
      return grouped;
    },
    async applyChanges() {
      if (!this.canApply) return;

      const confirmed = window.confirm(
        `Confermi l'aggiornamento di ${this.rows.length} consulenze tecniche da "Da fare" a "Scalata"?`,
      );
      if (!confirmed) return;

      this.resetFeedback();
      this.applying = true;

      let operationRef = null;
      const appliedItems = [];
      let updatedPercorsi = 0;

      try {
        operationRef = await addDoc(collection(this.db, OPERATIONS_COLLECTION), {
          kind: OPERATION_KIND,
          label: 'Scalata consulenze tecniche con check finale completato',
          createdAt: serverTimestamp(),
          createdByUid: this.userUid || null,
          createdByEmail: this.userEmail || null,
          status: 'running',
          totalCandidates: this.rows.length,
          totalApplied: 0,
          totalPercorsi: 0,
          restoredAt: null,
        });

        const grouped = this.groupRowsByPercorso(this.rows);
        const nowIso = new Date().toISOString();
        let batch = writeBatch(this.db);
        let batchCount = 0;

        for (const [percorsoId, groupRows] of grouped.entries()) {
          const percorsoRef = doc(this.db, 'percorsi', percorsoId);
          const snap = await getDoc(percorsoRef);
          if (!snap.exists()) continue;

          const data = snap.data() || {};
          const steps = Array.isArray(data.steps)
            ? data.steps.map((step) => (step && typeof step === 'object' ? { ...step } : step))
            : [];
          const sortedRows = [...groupRows].sort((a, b) => a.stepIndex - b.stepIndex);
          const rowByStepIndex = new Map(sortedRows.map((row) => [row.stepIndex, row]));
          const autoScalata = applyAutomaticTechnicalConsulenzeScalata(steps, {
            nowIso,
            allowedStepIndices: sortedRows.map((row) => row.stepIndex),
          });
          if (!autoScalata.changed) continue;

          autoScalata.changes.forEach((change) => {
            const row = rowByStepIndex.get(change.stepIndex) || {};
            appliedItems.push({
              percorsoId,
              stepIndex: change.stepIndex,
              previousStatus: change.previousStatus,
              previousDone: change.previousDone,
              previousDoneAt: change.previousDoneAt,
              previousScalataAt: change.previousScalataAt,
              nextStatus: change.nextStatus,
              nextDone: change.nextDone,
              nextDoneAt: change.nextDoneAt,
              nextScalataAt: change.nextScalataAt,
              uid: row.uid || null,
              userEmail: row.userEmail || null,
              programTitle: row.programTitle || normalizeText(data.title) || normalizeText(data.productName) || null,
              productName: row.productName || normalizeText(data.productName) || null,
              sequenceIndex: row.sequenceIndex || Number(data.sequenceIndex) || null,
              sequenceTotal: row.sequenceTotal || Number(data.sequenceTotal) || null,
              stepName: row.stepName || change.stepName || null,
              consulenzaType: row.rawConsulenzaType || change.rawConsulenzaType || null,
              checkStepIndex: change.checkIndex,
              checkName: row.checkLabel || change.checkName || null,
              checkDoneAt: change.checkDoneAt || null,
            });
          });

          batch.update(percorsoRef, { steps: autoScalata.steps });
          batchCount += 1;
          updatedPercorsi += 1;

          if (batchCount >= WRITE_BATCH_SIZE) {
            await batch.commit();
            batch = writeBatch(this.db);
            batchCount = 0;
          }
        }

        if (batchCount > 0) {
          await batch.commit();
        }

        if (appliedItems.length) {
          await this.saveOperationChunks(operationRef.id, appliedItems);
        }

        await updateDoc(operationRef, {
          status: 'completed',
          totalApplied: appliedItems.length,
          totalPercorsi: updatedPercorsi,
          appliedAt: serverTimestamp(),
        });

        if (appliedItems.length) {
          this.statusMessage = `Operazione completata: aggiornate ${appliedItems.length} consulenze su ${updatedPercorsi} programmi.`;
        } else {
          this.statusMessage = 'Nessuna consulenza aggiornata: i dati sono cambiati dopo la scansione.';
        }
      } catch (err) {
        console.error('applyChanges error', err);
        this.error = 'Errore durante l’applicazione della scalata.';
        if (operationRef) {
          try {
            if (appliedItems.length) {
              await this.saveOperationChunks(operationRef.id, appliedItems);
            }
            await updateDoc(operationRef, {
              status: 'failed',
              totalApplied: appliedItems.length,
              totalPercorsi: updatedPercorsi,
              errorMessage: normalizeText(err?.message || err),
            });
          } catch (secondaryErr) {
            console.error('applyChanges secondary error', secondaryErr);
          }
        }
      } finally {
        this.applying = false;
        await this.loadLatestOperation();
        await this.scanCandidates();
      }
    },
    async readOperationItems(operationId) {
      if (!operationId) return [];
      const chunkQuery = query(
        collection(this.db, OPERATIONS_COLLECTION, operationId, 'chunks'),
        orderBy(documentId()),
      );
      const snap = await getDocs(chunkQuery);
      const items = [];
      snap.forEach((docSnap) => {
        const data = docSnap.data() || {};
        const chunk = Array.isArray(data.items) ? data.items : [];
        chunk.forEach((item) => items.push(item));
      });
      return items;
    },
    async restoreLatestOperation() {
      if (!this.canRestore || !this.latestOperation) return;

      const confirmed = window.confirm(
        `Confermi il ripristino dell'ultima operazione (${this.latestOperation.totalApplied || 0} consulenze)?`,
      );
      if (!confirmed) return;

      this.resetFeedback();
      this.restoring = true;

      let restoredCount = 0;
      let skippedCount = 0;
      let updatedPercorsi = 0;

      try {
        const items = await this.readOperationItems(this.latestOperation.id);
        const grouped = this.groupRowsByPercorso(items);
        let batch = writeBatch(this.db);
        let batchCount = 0;

        for (const [percorsoId, groupItems] of grouped.entries()) {
          const percorsoRef = doc(this.db, 'percorsi', percorsoId);
          const snap = await getDoc(percorsoRef);
          if (!snap.exists()) {
            skippedCount += groupItems.length;
            continue;
          }

          const data = snap.data() || {};
          const steps = Array.isArray(data.steps)
            ? data.steps.map((step) => (step && typeof step === 'object' ? { ...step } : step))
            : [];
          let changed = false;

          [...groupItems].sort((a, b) => a.stepIndex - b.stepIndex).forEach((item) => {
            const step = steps[item.stepIndex];
            if (!step || typeof step !== 'object') {
              skippedCount += 1;
              return;
            }

            const currentStatus = this.normalizeConsulenzaStatus(step.consulenzaStatus);
            if (currentStatus !== this.normalizeConsulenzaStatus(item.nextStatus || 'Scalata')) {
              skippedCount += 1;
              return;
            }

            steps[item.stepIndex] = {
              ...step,
              consulenzaStatus: item.previousStatus || 'Da fare',
              done: !!item.previousDone,
              doneAt: item.previousDoneAt || null,
              scalataAt: item.previousScalataAt || null,
            };
            restoredCount += 1;
            changed = true;
          });

          if (!changed) continue;

          batch.update(percorsoRef, { steps });
          batchCount += 1;
          updatedPercorsi += 1;

          if (batchCount >= WRITE_BATCH_SIZE) {
            await batch.commit();
            batch = writeBatch(this.db);
            batchCount = 0;
          }
        }

        if (batchCount > 0) {
          await batch.commit();
        }

        await updateDoc(doc(this.db, OPERATIONS_COLLECTION, this.latestOperation.id), {
          restoredAt: serverTimestamp(),
          restoredByUid: this.userUid || null,
          restoredByEmail: this.userEmail || null,
          restoreStatus: 'completed',
          restoredCount,
          restoreSkippedCount: skippedCount,
          restoredPercorsi: updatedPercorsi,
        });

        this.statusMessage = skippedCount
          ? `Ripristino completato: ${restoredCount} consulenze ripristinate, ${skippedCount} saltate perche modificate nel frattempo.`
          : `Ripristino completato: ${restoredCount} consulenze ripristinate.`;
      } catch (err) {
        console.error('restoreLatestOperation error', err);
        this.error = 'Errore durante il ripristino dell’ultima operazione.';
      } finally {
        this.restoring = false;
        await this.loadLatestOperation();
        await this.scanCandidates();
      }
    },
  },
};
</script>

<style scoped>
.tool-card {
  margin: 16px 0;
  padding: 18px 20px;
  border-radius: 18px;
  border: 1px solid #dbe2f0;
  background: #ffffff;
  display: grid;
  gap: 16px;
}

.toolbar {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.toolbar-group {
  display: grid;
  gap: 6px;
}

.toolbar-group--wide {
  align-content: start;
}

.toolbar-label {
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64748b;
}

.toolbar-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.btn {
  border: 0;
  border-radius: 12px;
  padding: 10px 16px;
  font-weight: 700;
  cursor: pointer;
  color: #ffffff;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn.primary {
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
}

.btn.success {
  background: linear-gradient(135deg, #0f766e, #047857);
}

.btn.warning {
  background: linear-gradient(135deg, #b45309, #92400e);
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.stat-card {
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 14px 16px;
  background: #f8fafc;
  display: grid;
  gap: 6px;
}

.stat-card--accent {
  background: rgba(37, 99, 235, 0.08);
  border-color: rgba(37, 99, 235, 0.18);
}

.stat-card__label {
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64748b;
}

.stat-card strong {
  font-size: 1.5rem;
  color: #0f172a;
}

.tool-state {
  margin: 14px 0;
  padding: 14px 16px;
  border-radius: 14px;
  border: 1px solid #dbe2f0;
  background: #f8fafc;
  color: #334155;
}

.tool-state--error {
  border-color: #fecaca;
  background: #fff1f2;
  color: #b91c1c;
}

.tool-state--success {
  border-color: #bbf7d0;
  background: #f0fdf4;
  color: #166534;
}

.table-wrap {
  margin-top: 16px;
  overflow-x: auto;
}

.consulenze-table {
  width: 100%;
  min-width: 1100px;
  border-collapse: collapse;
  background: #ffffff;
  border-radius: 18px;
  overflow: hidden;
}

.consulenze-table th,
.consulenze-table td {
  padding: 12px 10px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: middle;
}

.consulenze-table th {
  background: #f8fafc;
  color: #1f2937;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.cell-main {
  color: #0f172a;
  font-weight: 600;
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}

.muted {
  color: #64748b;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid transparent;
  font-size: 0.8rem;
  font-weight: 700;
}

.status-pill--todo {
  background: #fef3c7;
  border-color: #fde68a;
  color: #b45309;
}

@media (max-width: 1080px) {
  .toolbar,
  .stats-grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 720px) {
  .toolbar,
  .stats-grid {
    grid-template-columns: 1fr;
  }

  .toolbar-actions {
    flex-direction: column;
  }
}
</style>
